import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import EditLayout from "../layout/EditLayout";
import RecordViewNewRecordPicker, {
  fetchRecordViewChoicesForNewRecord,
  resolveEmitPayload,
} from "../layout/RecordViewNewRecordPicker";

function EditView() {
  const { objectName, id } = useParams();
  const location = useLocation();
  const [recordViewExtras, setRecordViewExtras] = useState({
    recordviewId: "",
    editLayoutId: "",
  });
  const [rvResolvedOnce, setRvResolvedOnce] = useState(false);
  const [pickRequired, setPickRequired] = useState(null);
  const [rvMultiOptions, setRvMultiOptions] = useState(null);

  // Determine source page from URL or state
  let sourcePage = 'listview'; // default

  // First check if sourcePage is explicitly set in state
  if (location.state?.sourcePage) {
    sourcePage = location.state.sourcePage;
  } else if (location.state?.from) {
    sourcePage = location.state.from;
  } else {
    // Try to determine from referrer or URL pattern
    const referrer = document.referrer;
    if (referrer.includes('/view/') || referrer.includes('/detail/')) {
      sourcePage = 'detail';
    } else if (referrer.includes('/listview/') || referrer.includes('/list/')) {
      sourcePage = 'listview';
    }
  }

  // Extract query parameters for pre-population
  const searchParams = new URLSearchParams(location.search);
  const targetfield = searchParams.get('targetfield');
  const targetid = searchParams.get('targetid');
  const parentobjectname = searchParams.get('parentobjectname');

  const basePrePopulate = useMemo(() => {
    const o = {};
    if (targetfield && targetid) {
      o.targetField = targetfield;
      o.targetId = targetid;
      if (parentobjectname) o.parentObjectName = parentobjectname;
    }
    const urlRv = searchParams.get('recordviewId') || searchParams.get('recordviewid');
    const urlLayout = searchParams.get('editLayoutId') || searchParams.get('editlayoutid');
    if (urlRv) o.recordviewId = urlRv;
    if (urlLayout) o.editLayoutId = urlLayout;
    return Object.keys(o).length ? o : null;
  }, [targetfield, targetid, parentobjectname, location.search]);

  const showRvPicker =
    !id &&
    objectName &&
    String(objectName).toLowerCase() !== 'email_log';

  useEffect(() => {
    if (id) {
      setRvResolvedOnce(true);
      setPickRequired(null);
      setRvMultiOptions(null);
    } else {
      setRvResolvedOnce(false);
      setRecordViewExtras({ recordviewId: '', editLayoutId: '' });
      setPickRequired(null);
      setRvMultiOptions(null);
    }
  }, [id, objectName]);

  const handleRecordViewResolved = useCallback((payload) => {
    setRecordViewExtras(payload || { recordviewId: '', editLayoutId: '' });
    setRvResolvedOnce(true);
  }, []);

  useEffect(() => {
    if (id || !showRvPicker || rvResolvedOnce) return;
    if (pickRequired === true) return;

    let cancelled = false;
    (async () => {
      try {
        const { allowed, fallbackLayout } = await fetchRecordViewChoicesForNewRecord(objectName);
        if (cancelled) return;
        if (allowed.length === 0) {
          handleRecordViewResolved({ recordviewId: '', editLayoutId: fallbackLayout });
        } else if (allowed.length === 1) {
          handleRecordViewResolved(resolveEmitPayload(String(allowed[0].id), ''));
        } else {
          setRvMultiOptions(allowed);
          setPickRequired(true);
        }
      } catch (e) {
        if (!cancelled) handleRecordViewResolved({ recordviewId: '', editLayoutId: '' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, objectName, showRvPicker, rvResolvedOnce, pickRequired, handleRecordViewResolved]);

  const mergedQueryParams = useMemo(() => {
    const base = { ...(basePrePopulate && typeof basePrePopulate === 'object' ? basePrePopulate : {}) };
    if (recordViewExtras.recordviewId) {
      base.recordviewId = recordViewExtras.recordviewId;
      if (!String(recordViewExtras.editLayoutId || '').trim()) {
        delete base.editLayoutId;
      }
    }
    if (String(recordViewExtras.editLayoutId || '').trim()) {
      base.editLayoutId = String(recordViewExtras.editLayoutId).trim();
    }
    return Object.keys(base).length ? base : null;
  }, [basePrePopulate, recordViewExtras]);

  console.log('EditView - Query parameters:', { targetfield, targetid });
  console.log("Object Edit Wrapper-->", { objectName, id, sourcePage });

  const rvPrefetching = showRvPicker && !rvResolvedOnce && pickRequired === null;

  return (
    <div className="edit-view-wrapper">
      {rvPrefetching && (
        <div className="d-flex justify-content-center align-items-center gap-2 py-5 text-muted">
          <Spinner animation="border" size="sm" />
          <span>Loading…</span>
        </div>
      )}
      {showRvPicker && pickRequired === true && !rvResolvedOnce && (
        <div className="container py-4" style={{ maxWidth: 480 }}>
          <h1 className="h5 mb-1">New record</h1>
          <p className="text-muted small mb-3">
            Choose a record view for <span className="text-body fw-medium">{objectName}</span>, then click Next to open the form.
          </p>
          <RecordViewNewRecordPicker
            objectName={objectName}
            allowedChoices={rvMultiOptions}
            initialRecordviewId={
              basePrePopulate && basePrePopulate.recordviewId
                ? String(basePrePopulate.recordviewId)
                : ''
            }
            onResolved={handleRecordViewResolved}
          />
        </div>
      )}
      {(!showRvPicker || rvResolvedOnce) && (
        <EditLayout
          key={`edit-page-${objectName}-${recordViewExtras.recordviewId || 'norv'}-${recordViewExtras.editLayoutId || 'nolayout'}`}
          objectName={objectName}
          id={id}
          sourcePage={sourcePage}
          queryParams={mergedQueryParams}
        />
      )}
    </div>
  );
}
export default EditView;
